// Layer 3 (Transformation): pure projection for the plant-circuits canvas
// (paul-call.A2 #252 - paul-call.A3 fence - paul-call.A7 #255,
// docs/logic/ingestion/plant-model.md section UI states Circuits / Circuit open).
// No React, no engine mutation -- every status comes from `spcRead`.

import type { SessionState } from '@/versions/v1/schema/scenario';
import type { ExcursionEvent, SignalCell } from '@/versions/v1/schema/spc';
import type { Case } from '@/versions/v1/schema/case';
import { MONITORED_VARIABLES, TAG_MAPPING } from '@/versions/v1/shared/bunker-hill-spc';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { controlChart, formatSpcValue, spcRead } from '@/versions/v1/shared/SpcEngine';
import type { ControlChartViewModel } from '@/versions/v1/shared/SpcEngine';
import { valueAt } from '@/versions/v1/shared/series.derive';
import { readingComparisonBasis } from '@/versions/v1/shared/comparison-basis';
import { STAGES, type StageId, type Substage } from '@/versions/v1/modules/overview/StageLadder.transform';
import { rankExcursions } from '@/versions/v1/shared/alertGovernance';
import { CASES, VARIATION_EVENTS } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { INITIAL_ADVISORY_STATE } from '@/versions/v1/shared/AdvisoryEngine';
import { applyCases } from '@/versions/v1/shared/ScenarioEngine';
import { diagnose } from '@/versions/v1/shared/DiagnosisEngine';
import { mergeCasesForThreading, threadsFor } from '@/versions/v1/shared/threads';

// CI-F10 / CI-F11 (fix round 1): `DriftPanel.transform.ts` and
// `StageLadder.transform.ts` are frozen for this stream (Hard rules), so the
// governance-context plumbing (`rankExcursions`' threads/cases inputs) and
// the "which case is open for this variable" lookup are duplicated here
// rather than exported out of those files, which the brief's freeze does not
// license touching. Same seed, same computation, same helper functions --
// not a second ranking law, just a second call site.
const OPEN_CASE_STATES = new Set<Case['state']>([
  'detected',
  'matched',
  'routed',
  'evidence-requested',
  'escalated',
  'diagnosed',
  'outcome-documented',
  'fmea-review',
]);

function circuitGovernanceContext(state: SessionState) {
  const cases = applyCases(CASES, state, INITIAL_ADVISORY_STATE);
  const diagnosis = diagnose(state, INITIAL_ADVISORY_STATE);
  const threads = threadsFor(cases, diagnosis);
  const allCases = mergeCasesForThreading(cases, diagnosis);
  return { threads, allCases };
}

// CI-F28 (fix round 2, NOTE -- fixed as cheap): `circuitGovernanceContext`
// runs the diagnosis engine (`applyCases` + `diagnose` + `threadsFor` +
// `mergeCasesForThreading`); it was being rebuilt inside every `sortChips`
// call (once per stage, plus the tile roll-up, plus the pane) instead of
// once per `circuitsView`. Pure and correct either way -- no mutation, no
// staleness -- but wasteful. `GovernanceContext` lets `circuitsView` build
// it once and thread it down.
type GovernanceContext = ReturnType<typeof circuitGovernanceContext>;

function openCaseForVariable(variableId: string, cases: readonly Case[]): Case | undefined {
  return cases.find((c) => {
    if (!OPEN_CASE_STATES.has(c.state)) return false;
    const eventId = c.variationEventId;
    if (!eventId) return false;
    if (eventId.startsWith('ve-')) {
      const event = VARIATION_EVENTS.find((row) => row.id === eventId);
      return event?.variableId === variableId;
    }
    if (eventId.startsWith('ex-')) {
      return (
        eventId
          .slice(3)
          .replace(/-(six-point-trend|beyond-limit|widening-variability|cyclical)$/, '') ===
        variableId
      );
    }
    return false;
  });
}

export type CircuitId = 'grinding' | 'pb' | 'zn';

/**
 * Three circuits, flow order (Q1) -- the one authored constant, everything
 * else derived. `stages` names the `STAGES` (`StageLadder.transform.ts`)
 * rows that make up the circuit; `outcomeReadingId` is the circuit's last
 * stage's outcome (the figure the tile leads with).
 */
export const CIRCUITS: readonly {
  id: CircuitId;
  label: string;
  subprocessId: string;
  stages: readonly StageId[];
  outcomeReadingId: string;
}[] = [
  { id: 'grinding', label: 'Grinding', subprocessId: 'sp-grind', stages: ['grinding'], outcomeReadingId: 'rd-spec-p80' },
  { id: 'pb', label: 'Lead flotation', subprocessId: 'sp-pb', stages: ['pb-rougher', 'pb-cleaner'], outcomeReadingId: 'rd-kpi-pb-grade' },
  { id: 'zn', label: 'Zinc flotation', subprocessId: 'sp-zn', stages: ['zn-rougher', 'zn-cleaner'], outcomeReadingId: 'rd-kpi-zn-grade' },
];

/** The grinding-circuit feed instrument (mv-wit-021 / WIT-021) -- the
 * upstream chart Q5 always shows beside an off circuit's own parameters. */
export const UPSTREAM_VARIABLE_ID = 'mv-wit-021';

const MONITORED_VARIABLE_BY_READING = new Map(MONITORED_VARIABLES.map((v) => [v.readingId, v]));

const CELL_RANK: Record<SignalCell | 'not-computed', number> = {
  consequence: 3,
  drift: 2,
  'not-capable': 1,
  quiet: 0,
  'not-computed': 0,
};

export type ParamChip = {
  readingId: string;
  variableId?: string;
  label: string;
  value: string;
  unit: string;
  cell: SignalCell | 'not-computed' | 'no-series';
  basisLabel?: string;
  basisValue?: number;
  since?: string;
  /** Full ISO timestamp behind `since` -- CI-F16: the 7 d pane needs a date,
   * not just the clock, to disambiguate which of seven evenings. */
  detectedAtIso?: string;
  /** CI-F11: the underlying excursion, kept so the pane/stage sort can rank
   * through `rankExcursions` (the drift panel's own sort) instead of a
   * bespoke z-score. Undefined for `not-capable` chips (no excursion event)
   * and for quiet/not-computed chips. */
  excursion?: ExcursionEvent;
  isOutcome: boolean;
};

export type StageNode = {
  id: string;
  label: string;
  worstCell: SignalCell | 'not-computed';
  /** Non-quiet params first (worst first), then the stage's outcome if it
   * did not already appear among them. Remaining quiet params collapse
   * behind `quietCount` (Q4 rule of three). */
  params: readonly ParamChip[];
  quietCount: number;
  /** CI-F3: readings with no `MonitoredVariable` at all -- kept separate
   * from `quietCount` so the glass never claims an uncomputed reading is
   * "in band". */
  notComputedCount: number;
  nodeId: string;
};

export type OffChart = {
  variableId: string;
  label: string;
  /** Omitted (undefined) when the reading carries no real instrument tag --
   * only a KPI code (CI-F9): no id invented for the glass. */
  tag?: string;
  since?: string;
  caseId?: string;
  /** The open case's worked-example kind — `hardness` is the page's own
   *  scenario, already named by the Grinding bottleneck read. */
  caseKind?: string;
  /** CI-F7: set only on the upstream chart. `'same window'` when its points
   * actually cover the pane's window; otherwise the true span the seed can
   * honestly support, so the caption never claims a coverage the axis
   * doesn't show. */
  windowNote?: string;
  chart: ControlChartViewModel;
};

export type OffPane = {
  window: '24h' | '7d';
  charts: readonly OffChart[];
  upstream: OffChart;
};

export type CircuitTile = {
  id: CircuitId;
  label: string;
  rag: 'green' | 'amber' | 'red';
  worstCell: SignalCell | 'not-computed';
  worstLabel?: string;
  /** CI-F1: the circuit's stage names in flow order (the comp's tile line 2)
   * -- e.g. "Conditioner · Rougher · Regrind · Cleaners". */
  stageNamesLine: string;
  outcome: { value: string; unit: string; basisLabel?: string; basisValue?: number };
  variableCount: number;
  quietCount: number;
  /** CI-F3: see `StageNode.notComputedCount`. */
  notComputedCount: number;
  subprocessId: string;
  nodeId: string;
};

export type OpenCircuit = {
  id: CircuitId;
  label: string;
  rag: 'green' | 'amber' | 'red';
  stages: readonly StageNode[];
  pane?: OffPane;
};

export type ContextStub = {
  id: string;
  label: string;
};

/** Context stages Paul named around the three circuits (Q3) -- muted, no
 * RAG, not clickable; the plant's own flow order. */
export const CONTEXT_STUBS: readonly ContextStub[] = [
  { id: 'sp-crush', label: 'Crushing' },
  { id: 'sp-filter', label: 'Thickening & filtration' },
  { id: 'sp-paste', label: 'Paste' },
  { id: 'sp-water', label: 'Water' },
];

export type CircuitsView = {
  circuits: readonly CircuitTile[];
  context: readonly ContextStub[];
  open?: OpenCircuit;
};

function raisedAtClock(at: string): string {
  return at.split('T')[1] ?? at;
}

/** A reading's chip -- SPC-backed when it has a `MonitoredVariable`
 * (`bunker-hill-spc.ts`), else `not-computed` (honest, no signal invented). */
function paramChip(readingId: string, state: SessionState, isOutcome: boolean): ParamChip | undefined {
  const reading = readingByIdLookup(readingId);
  if (!reading) return undefined;
  const value = valueAt(readingId, state);
  const variable = MONITORED_VARIABLE_BY_READING.get(readingId);

  if (value === undefined) {
    return {
      readingId,
      label: reading.label,
      value: 'no series at this seed',
      unit: '',
      cell: 'no-series',
      isOutcome,
    };
  }

  if (!variable) {
    return {
      readingId,
      label: reading.label,
      value: formatSpcValue(reading, value),
      unit: reading.unit,
      cell: 'not-computed',
      isOutcome,
    };
  }

  const read = spcRead(variable.id, state);
  const cell: SignalCell | 'not-computed' = read.signal?.cell ?? 'not-computed';
  const basis = readingComparisonBasis(reading);
  const excursion = read.events.find((e) => e.variableId === variable.id);
  const nonQuietCell = cell !== 'quiet' && cell !== 'not-computed';

  return {
    readingId,
    variableId: variable.id,
    label: reading.label,
    value: formatSpcValue(reading, value),
    unit: reading.unit,
    cell,
    basisLabel: basis?.label,
    basisValue: basis?.value,
    since: nonQuietCell && excursion ? raisedAtClock(excursion.detectedAt) : undefined,
    detectedAtIso: nonQuietCell && excursion ? excursion.detectedAt : undefined,
    excursion: nonQuietCell ? excursion : undefined,
    isOutcome,
  };
}

function cellSeverity(cell: ParamChip['cell']): number {
  if (cell === 'no-series') return -1;
  return CELL_RANK[cell];
}

/** CI-F11: worst-first through the same law the drift panel uses --
 * `rankExcursions` (severity + bottleneck proximity + case/thread linkage),
 * not a bespoke z-score. Chips without an excursion (quiet, not-computed,
 * or a `not-capable` cell with no excursion event) score 0 and fall back to
 * `cellSeverity`, which already orders consequence > drift > not-capable. */
function sortChips(chips: readonly ParamChip[], state: SessionState, ctx: GovernanceContext): ParamChip[] {
  const { threads, allCases } = ctx;
  const excursionList = chips
    .map((c) => c.excursion)
    .filter((e): e is ExcursionEvent => e !== undefined);
  const ranked = rankExcursions(excursionList, state, threads, allCases);
  const scoreById = new Map(ranked.map((r) => [r.excursion.id, r.score]));
  const scoreFor = (chip: ParamChip) => (chip.excursion ? (scoreById.get(chip.excursion.id) ?? 0) : 0);
  return [...chips].sort((a, b) => {
    const sevDiff = cellSeverity(b.cell) - cellSeverity(a.cell);
    if (sevDiff !== 0) return sevDiff;
    const scoreDiff = scoreFor(b) - scoreFor(a);
    if (scoreDiff !== 0) return scoreDiff;
    return 0; // stable -- seed order preserved
  });
}

function worstOf(cells: readonly (SignalCell | 'not-computed')[]): SignalCell | 'not-computed' {
  let worst: SignalCell | 'not-computed' = 'quiet';
  let rank = -1;
  for (const cell of cells) {
    const score = CELL_RANK[cell];
    if (score > rank) {
      rank = score;
      worst = cell;
    }
  }
  return worst;
}

/** RAG map (Q1): quiet -> green; drift, not-capable -> amber; consequence ->
 * red; `not-computed` counts as quiet for the roll-up. */
export function ragForCell(cell: SignalCell | 'not-computed'): 'green' | 'amber' | 'red' {
  if (cell === 'consequence') return 'red';
  if (cell === 'drift' || cell === 'not-capable') return 'amber';
  return 'green';
}

function stagesForCircuit(circuitStageIds: readonly StageId[]) {
  return STAGES.filter((s) => circuitStageIds.includes(s.id));
}

function substageNode(
  substage: Substage,
  stageOutcomeReadingId: string,
  state: SessionState,
  ctx: GovernanceContext,
): StageNode {
  const allChips = substage.readingIds
    .map((id) => paramChip(id, state, id === stageOutcomeReadingId))
    .filter((c): c is ParamChip => c !== undefined);

  const withSeries = allChips.filter((c) => c.cell !== 'no-series');
  const nonQuiet = sortChips(
    withSeries.filter((c) => c.cell !== 'quiet' && c.cell !== 'not-computed'),
    state,
    ctx,
  );
  const outcomeChip = withSeries.find((c) => c.isOutcome);
  const shownIds = new Set(nonQuiet.map((c) => c.readingId));
  const trailer = outcomeChip && !shownIds.has(outcomeChip.readingId) ? [outcomeChip] : [];
  const visible = [...nonQuiet, ...trailer];
  const visibleIds = new Set(visible.map((c) => c.readingId));
  const hidden = withSeries.filter((c) => !visibleIds.has(c.readingId));
  // CI-F3: a `not-computed` reading (no `MonitoredVariable`) never counts as
  // "in band" -- it gets its own count, kept out of `quietCount`.
  const quietCount = hidden.filter((c) => c.cell === 'quiet').length;
  const notComputedCount = hidden.filter((c) => c.cell === 'not-computed').length;

  return {
    id: substage.id,
    label: substage.label,
    worstCell: worstOf(withSeries.map((c) => (c.cell === 'no-series' ? 'not-computed' : c.cell))),
    params: visible,
    quietCount,
    notComputedCount,
    nodeId: '', // filled by the caller (the circuit's subprocess id)
  };
}

function outcomeFigure(readingId: string, state: SessionState) {
  const reading = readingByIdLookup(readingId);
  const value = valueAt(readingId, state);
  if (!reading || value === undefined) return { value: 'no series at this seed', unit: '' };
  const basis = readingComparisonBasis(reading);
  return {
    value: formatSpcValue(reading, value),
    unit: reading.unit,
    basisLabel: basis?.label,
    basisValue: basis?.value,
  };
}

const WINDOW_MS = { '24h': 24 * 60 * 60 * 1000, '7d': 7 * 24 * 60 * 60 * 1000 } as const;

/** Q5: `controlChart` carries no window option (checked -- every point
 * comes back), so the window is applied here, in the transform, by
 * filtering `points` to `[cursor - span, cursor]`. Everything else on the
 * view model (limits, caption, events) stays the full-history read -- only
 * the drawn series is windowed. */
function windowedChart(variableId: string, state: SessionState, span: '24h' | '7d'): ControlChartViewModel {
  const chart = controlChart(variableId, state);
  const cursorMs = Date.parse(state.cursor);
  const startMs = cursorMs - WINDOW_MS[span];
  return {
    ...chart,
    points: chart.points.filter((p) => {
      const t = Date.parse(p.at);
      return Number.isNaN(t) || (t >= startMs && t <= cursorMs);
    }),
  };
}

/** CI-F9: a reading's `tag` field can be a KPI code (`kpi-ag-grade`), not a
 * plant instrument -- the same convention `SetupScene.transform.ts`'s
 * `tagGlassLabel` already draws. The real instrument/loop tag, when one
 * exists, lives on `TAG_MAPPING`.
 *
 * Review priority-likelihood M5: prefer `instrumentTag` over `loopTag`. A
 * chart caption names the instrument that measures the charted variable --
 * a control loop is a different thing (it can span several instruments and
 * an actuator) and showing it here mis-names what the reader is looking at.
 * `zn-conditioner-ph`'s row keeps `loopTag: 'AIC5002'` (the alarm boundary,
 * docs/engines/diagnosis.md § Alarm boundary, resolves the Zn pH alarm to
 * its signal through that exact tag) -- this function just stops reading it
 * for a caption. */
function instrumentTagFor(readingId: string): string | undefined {
  const row = TAG_MAPPING.find((r) => r.readingId === readingId);
  const raw = row?.instrumentTag ?? row?.loopTag;
  if (!raw || raw.startsWith('kpi-')) return undefined;
  return raw;
}

/** CI-F16: on the 7 d pane, "since 18:00" names one of seven evenings --
 * carry the date too. The 24 h pane's clock-only convention (the ladder's
 * own) stays unambiguous and unchanged. */
function formatSince(iso: string, span: '24h' | '7d'): string {
  const clock = raisedAtClock(iso);
  if (span === '24h') return clock;
  const datePart = iso.split('T')[0] ?? '';
  const parsed = new Date(`${datePart}T00:00:00Z`);
  const dateLabel = Number.isNaN(parsed.getTime())
    ? datePart
    : parsed.toLocaleDateString('en-US', { day: '2-digit', month: 'short', timeZone: 'UTC' });
  return `${dateLabel} ${clock}`;
}

function offChart(
  chip: ParamChip,
  state: SessionState,
  span: '24h' | '7d',
  allCases: readonly Case[],
): OffChart | undefined {
  if (!chip.variableId) return undefined;
  const openCase = openCaseForVariable(chip.variableId, allCases);
  return {
    variableId: chip.variableId,
    label: chip.label,
    tag: instrumentTagFor(chip.readingId),
    since: chip.detectedAtIso ? formatSince(chip.detectedAtIso, span) : chip.since,
    caseId: openCase?.code ?? openCase?.id,
    caseKind: openCase?.kind,
    chart: windowedChart(chip.variableId, state, span),
  };
}

/** Q5: 24 h default; 7 d when the earliest non-quiet excursion's
 * `detectedAt` is more than a day before the cursor. */
function windowFor(nonQuiet: readonly ParamChip[], state: SessionState): '24h' | '7d' {
  const cursorMs = Date.parse(state.cursor);
  let earliest = Infinity;
  for (const chip of nonQuiet) {
    if (!chip.variableId) continue;
    const read = spcRead(chip.variableId, state);
    // CI-F19: `events` can carry more than one run; filter to this
    // variable's own events (defensive, matching `paramChip`'s convention)
    // and take the true earliest `detectedAt`, not `events[0]`.
    for (const event of read.events) {
      if (event.variableId !== chip.variableId) continue;
      const t = Date.parse(event.detectedAt);
      if (!Number.isNaN(t)) earliest = Math.min(earliest, t);
    }
  }
  if (earliest === Infinity) return '24h';
  return cursorMs - earliest > WINDOW_MS['24h'] ? '7d' : '24h';
}

/** CI-F1: the tile's stage-names line (the comp's tile line 2), e.g.
 * "Conditioner · Rougher · Regrind · Cleaners". */
function stageNamesLine(stages: readonly { substages: readonly Substage[] }[]): string {
  return stages
    .flatMap((s) => s.substages.map((sub) => sub.label))
    .join(' · ');
}

/** CI-F7: honest upstream caption -- "same window" only when the upstream
 * series' own points actually cover the pane's window; otherwise the true
 * span the seed can support, read off the windowed points themselves. */
function upstreamWindowNote(chart: ControlChartViewModel, state: SessionState, span: '24h' | '7d'): string {
  const cursorMs = Date.parse(state.cursor);
  const startMs = cursorMs - WINDOW_MS[span];
  const times = chart.points
    .map((p) => Date.parse(p.at))
    .filter((t) => !Number.isNaN(t));
  if (times.length === 0) return 'no data at this window';
  const earliest = Math.min(...times);
  const latest = Math.max(...times);
  const toleranceMs = 60 * 60 * 1000; // an hour of seed-cadence slop
  if (earliest <= startMs + toleranceMs) return 'same window';
  const fmt = (ms: number) =>
    new Date(ms).toLocaleString('en-US', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'UTC',
    });
  return `${fmt(earliest)} \u2192 ${fmt(latest)}`;
}

export function circuitsView(state: SessionState, opts?: { openId?: CircuitId }): CircuitsView {
  const ctx = circuitGovernanceContext(state);
  const { allCases } = ctx;
  const tiles: CircuitTile[] = CIRCUITS.map((circuit) => {
    const stages = stagesForCircuit(circuit.stages);
    const allReadingIds = stages.flatMap((s) => s.substages.flatMap((sub) => sub.readingIds));
    const chips = allReadingIds
      .map((id) => paramChip(id, state, id === circuit.outcomeReadingId))
      .filter((c): c is ParamChip => c !== undefined)
      .filter((c) => c.cell !== 'no-series');
    const nonQuiet = sortChips(
      chips.filter((c) => c.cell !== 'quiet' && c.cell !== 'not-computed'),
      state,
      ctx,
    );
    const worst = worstOf(chips.map((c) => (c.cell === 'no-series' ? 'not-computed' : c.cell)));
    // CI-F3: split the roll-up the same way the stage cards do.
    const notComputed = chips.filter((c) => c.cell === 'not-computed');
    const quiet = chips.filter((c) => c.cell === 'quiet');
    return {
      id: circuit.id,
      label: circuit.label,
      rag: ragForCell(worst),
      worstCell: worst,
      worstLabel: nonQuiet[0]?.label,
      stageNamesLine: stageNamesLine(stages),
      outcome: outcomeFigure(circuit.outcomeReadingId, state),
      variableCount: chips.length,
      quietCount: quiet.length,
      notComputedCount: notComputed.length,
      subprocessId: circuit.subprocessId,
      nodeId: circuit.subprocessId,
    };
  });

  let open: OpenCircuit | undefined;
  const openCircuit = CIRCUITS.find((c) => c.id === opts?.openId);
  if (openCircuit) {
    const tile = tiles.find((t) => t.id === openCircuit.id)!;
    const stageRows = stagesForCircuit(openCircuit.stages);
    const stageNodes = stageRows.flatMap((stage) =>
      stage.substages.map((sub) => ({
        ...substageNode(sub, stage.outcomeReadingId, state, ctx),
        nodeId: openCircuit.subprocessId,
      })),
    );

    let pane: OffPane | undefined;
    if (tile.rag !== 'green') {
      const allReadingIds = stageRows.flatMap((s) => s.substages.flatMap((sub) => sub.readingIds));
      const chips = allReadingIds
        .map((id) => paramChip(id, state, id === openCircuit.outcomeReadingId))
        .filter((c): c is ParamChip => c !== undefined)
        .filter((c) => c.cell !== 'no-series');
      const nonQuiet = sortChips(
        chips.filter((c) => c.cell !== 'quiet' && c.cell !== 'not-computed'),
        state,
        ctx,
      );
      const window = windowFor(nonQuiet, state);
      const charts = nonQuiet
        .map((c) => offChart(c, state, window, allCases))
        .filter((c): c is OffChart => c !== undefined);
      const upstreamVariable = MONITORED_VARIABLES.find((v) => v.id === UPSTREAM_VARIABLE_ID)!;
      const upstreamReading = readingByIdLookup(upstreamVariable.readingId);
      const upstreamChart = windowedChart(UPSTREAM_VARIABLE_ID, state, window);
      pane = {
        window,
        charts,
        upstream: {
          variableId: UPSTREAM_VARIABLE_ID,
          label: upstreamReading?.label ?? 'Mill feed',
          tag: instrumentTagFor(upstreamVariable.readingId) ?? upstreamReading?.tag,
          windowNote: upstreamWindowNote(upstreamChart, state, window),
          chart: upstreamChart,
        },
      };
    }

    open = { id: openCircuit.id, label: openCircuit.label, rag: tile.rag, stages: stageNodes, pane };
  }

  return { circuits: tiles, context: CONTEXT_STUBS, open };
}
